import { sortBy } from "lodash";
import moment from "moment";
import * as React from "react";

import { DisplayOptions } from "./DisplayOptions";

import { CountyProjectContainerState, CountyProjectContext, CountyProjectContextProps, ProjectOptions } from "@app/projects";
import { IconToggle } from "@app/shared";

import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { AdequacyTrendChartItem } from "@app/projects/results/common";

import { ProjectRunConfigurationStatus, ProjectRunConfigurationType, TimeFrequency, AttestationRateDays } from "@app/projects/enumerators";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { CheckboxDropdown, DropdownItem } from "@app/shared/checkbox-dropdown";
import { TrendChartItemType } from "@app/velvet/enumerators";
import { TableType, ViewableFieldsContextProps, withViewableFields } from "@app/viewable-fields";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ButtonToggle } from "../../shared/ButtonToggle";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import features from "@app/feature-flags/features";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";

type OptionsProps = ViewableFieldsContextProps & FeatureFlagContextProps & {
  defaultProjectOptionsOpen: boolean;
  disabled: boolean;
  isReadOnly: boolean;
  showGraphicalArea: boolean;
  onShowGraphicalAreaClick: () => void;
  hasProspectOrMarketProviders: boolean;
  onDisplayTrendChartChange: () => void;
  showTrendChart: boolean;
  showTrendChartToggle: boolean;
};

type OptionsContentProps = OptionsProps & CountyProjectContextProps;

interface OptionsState {
  displayOptionsDirty: boolean;
  projectOptionsDirty: boolean;
  selectedMemberSetAliases: Array<DropdownItem<ProjectRunConfigurationType>>;
  triggerClose: () => void;
}

const Options: React.FC<React.PropsWithChildren<OptionsProps>> = (props: OptionsProps) => {
  return (
    <CountyProjectContext.Consumer>
      {(contextProps) => (
        <OptionsContent {...props} {...contextProps} />
      )}
    </CountyProjectContext.Consumer>
  );
};

class OptionsContent extends React.Component<OptionsContentProps, OptionsState> {
  constructor(props: OptionsContentProps) {
    super(props);

    this.state = {
      displayOptionsDirty: false,
      projectOptionsDirty: false,
      selectedMemberSetAliases: [],
      triggerClose: null,
    };
  }

  public render() {
    if (!this.props.runConfiguration) { //nothing to display if there's no run configuration
      return null;
    }

    const { compareToRunConfiguration, setRunConfiguration, adequacyTrendChartData, module, project, runConfiguration, runFrequency, attestationRateDays, adequacyResult, accuracyResult, regionFilters, isDualMembershipMode, setDualMembershipMode, setLoading } = this.props;

    //Whether or not we're currently comparing to a run configuration
    const isComparingResults = !!compareToRunConfiguration;
    //The priorRunConfigurationId to set when the toggle is clicked
    const priorRunConfigurationId = isComparingResults
      ? undefined
      : this.getCompareToRunConfigurationId(adequacyTrendChartData, runConfiguration);
    //Whether or not we should show the toggle at all
    const shouldShowCompareToggle = (isComparingResults || priorRunConfigurationId) && !isDualMembershipMode && !this.props.cmsCreditIsApplying && module !== "Accuracy";

    const haveResultsToRender: boolean = !!runConfiguration && ((!!adequacyResult && !!adequacyResult.details && adequacyResult.details.length > 0) || (!!accuracyResult && !!accuracyResult.details && accuracyResult.details.length > 0));

    const isLowestGranularity: boolean = !!regionFilters.specialty && (!!regionFilters.county || !!regionFilters.state);
    const viewablePanels: TableType[] = [];
    let showDisplayOptions = false;

    if (haveResultsToRender && isLowestGranularity && !isComparingResults) {
      showDisplayOptions = true;

      if (module === "Accuracy") {
        viewablePanels.push(TableType.AccuracyProviders);
      } else {
        viewablePanels.push(TableType.NetworkProviders);

        if (runConfiguration.showNextClosestNetworkProviders && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed) {
          viewablePanels.push(TableType.NextClosestNetworkProviders);
        }
        viewablePanels.push(TableType.ProviderTerminations);

        if (runConfiguration.prospectProviderSetId && runConfiguration.prospectProviderSetVersionId) {
          viewablePanels.push(TableType.ProspectProviders);
        }
        if (runConfiguration.showNextClosestNetworkProviders && runConfiguration.prospectProviderSetId && runConfiguration.prospectProviderSetVersionId && !this.props.hasProspectOrMarketProviders && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed) {
          viewablePanels.push(TableType.NextClosestProspectProviders);
        }
        if (runConfiguration.useMarketProvidersForImpact) {
          viewablePanels.push(TableType.MarketProviders);
        }
        if (runConfiguration.showNextClosestNetworkProviders && runConfiguration.useMarketProvidersForImpact && !this.props.hasProspectOrMarketProviders && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed) {
          viewablePanels.push(TableType.NextClosestMarketProviders);
        }
      }
    }

    const memberSetDropdownItems = this.getMemberSets(project, runConfiguration);
    const selectedMemberAliases = this.getSelectedMemberSets(runConfiguration, isDualMembershipMode, compareToRunConfiguration);

    const timeViewDropdownItems: Array<DropdownItem<TimeFrequency>> = [
      {
        key: TimeFrequency.All,
        name: "Refreshes",
      },
      {
        key: TimeFrequency.Monthly,
        name: "Monthly Trends",
      },
    ];

    const AttestationRateDaysDropdownItems: Array<DropdownItem<AttestationRateDays>> = [
      {
        key: AttestationRateDays.Ninety,
        name: `Last ${AttestationRateDays.Ninety} Days`,
      },
      {
        key: AttestationRateDays.ThreeSixtyFive,
        name: `Last ${AttestationRateDays.ThreeSixtyFive} Days`,
      },
    ];

    const shouldDisable = (runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.Failed) ||
      (project.isCompanionRunning && !this.props.isReadOnly) ||
      !runConfiguration.companionProjectRunConfigurationId ||
      !project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId ===
        runConfiguration.companionProjectRunConfigurationId);
    return (
      <div className="row overview-options pt-3 d-flex justify-content-between">
        <div>
          {runConfiguration &&
            project.projectRunConfigurations.some((prc) => !!prc.companionProjectRunConfigurationId) &&
            runConfiguration.memberSetAlias &&
            module !== "Accuracy" &&
            !(!isDualMembershipMode && compareToRunConfiguration) && (
              <CheckboxDropdown
                buttonText={isDualMembershipMode ? "Display: Both Memberships" : `Display: ${runConfiguration.memberSetAlias}`}
                cancelText="Cancel"
                disabled={shouldDisable}
                disableEmptySelection={true}
                loading={this.props.disabled && !this.props.isReadOnly}
                items={memberSetDropdownItems}
                onSubmit={(selectedItems: Array<DropdownItem<ProjectRunConfigurationType>>) => this.handleMembershipChange(selectedItems, setDualMembershipMode, setLoading)}
                selectedItems={selectedMemberAliases}
                submitText="Submit"
              />
            )}
        </div>

        <div>
          <div className="d-flex justify-content-end align-items-center flex-wrap flex-lg-nowrap">
            {this.props.enabledFeatureFlags.has(features.USE_90_DAY_ATTESTATIONS) && module === 'Accuracy' && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed &&
              (
                <NewActionMenu
                  buttonText={`Attestation Rate: ${AttestationRateDaysDropdownItems.find((item) => item.key === attestationRateDays).name}`}
                  buttonClassNames="qes-button-secondary"
                  key={attestationRateDays}
                  loading={this.props.disabled && !this.props.isReadOnly}
                  testId="options__attestation-rate-dropdown"
                >
                  {AttestationRateDaysDropdownItems.map((item) => {
                    const isSelected = item.key === attestationRateDays;
                    return (
                      <NewActionMenuItem
                        display={item.name}
                        isSelected={isSelected}
                        key={item.key}
                        onClick={() => this.handleAttestationRateDaysChange(item.key)}
                        testId={`options__attestation-rate-option--${item.key}`}
                      />
                    )
                  })}
                </NewActionMenu>
              )
            }
            {shouldShowCompareToggle && (
              <IconToggle
                active={isComparingResults}
                disabled={this.props.disabled && !this.props.isReadOnly}
                onToggle={
                  () => {
                    setRunConfiguration(priorRunConfigurationId, null, true)
                    this.switchToTrendChart();
                  }
                }
                activeIconClass={["fa", "fa-toggle-on"]}
                inActiveIconClass={["fa", "fa-toggle-off"]}
                baseClassName={["qes-button qes-button-secondary m-0 p-0 text-nowrap"]}>
                Compare Results
              </IconToggle>
            )}
            {runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed &&
              (
                <NewActionMenu
                  buttonText={`Run Frequency: ${timeViewDropdownItems.find((item) => item.key === runFrequency).name}`}
                  testId="options__run-frequency-dropdown"
                  buttonClassNames="qes-button-secondary"
                  loading={this.props.disabled && !this.props.isReadOnly}
                  key={runFrequency}
                >
                  {
                    timeViewDropdownItems.map((item) => {
                      const isSelected = item.key === runFrequency;
                      return (
                        <NewActionMenuItem
                          display={item.name}
                          isSelected={isSelected}
                          key={item.key}
                          onClick={() => this.handleRunFrequencyChange(item.key)}
                          testId="options__run-frequency-option--${item.key}"
                        />
                      )
                    })
                  }
                </NewActionMenu>
              )
            }
            {this.props.showTrendChartToggle &&
              <ButtonToggle
                handleClick={() => this.props.onDisplayTrendChartChange()}
                hideTooltips={true}
                isPrimarySelected={this.props.showTrendChart}
                minWidth="300px"
                primaryButtonLabel="Trend Chart"
                secondaryButtonLabel="View Map"
                testId="options-toggle-display"
                disabled={isComparingResults}
                disabledText={"Maps cannot be accessed while in Compare Mode"}
                tooltipTextOneLine={true}
              />
            }
            {showDisplayOptions && (
              <FlyoutLink
                content={{
                  body: this.renderDisplayOptions(project.projectId, viewablePanels),
                  title: "Display Options",
                }}
                modalContent={{
                  body: "You will lose unsaved changes. Continue with closing the sidebar?",
                  cancelButtonText: "Cancel",
                  confirmButtonText: "Continue",
                  title: "Warning",
                }}
                size="60"
                classNames={"qes-button-secondary text-nowrap" + (this.props.disabled && !this.props.isReadOnly ? " disabled" : "")}
                warnOnClose={this.state.displayOptionsDirty}
                testId="viewable-fields-flyout-link"
              >
                <i className="fa fa-sliders mlr10" />Display Options
              </FlyoutLink>
            )}
            <IconToggle active={this.props.showGraphicalArea} disabled={this.props.disabled && !this.props.isReadOnly} onToggle={this.props.onShowGraphicalAreaClick} activeIconClass={["fa", "fa-toggle-on"]} inActiveIconClass={["fa", "fa-toggle-off"]} baseClassName={["icon-toggle", "ml10-imp", "pl10-imp", "vertical-separator", "d-flex", "mt-2"]}><i className="fa fa-bar-chart" /></IconToggle>
          </div>
        </div>

        <div className="col-12">
          <hr className="mb-0" />
        </div>
      </div >
    );
  }

  private handleRunFrequencyChange = async (selectedFrequency: TimeFrequency) => {
    if (selectedFrequency === this.props.runFrequency) {
      return;
    }

    const primaryConfigState = await this.props.setRunConfiguration(this.props.runConfiguration.projectRunConfigurationId, moment.utc(this.props.runConfiguration.dateCreated), false, selectedFrequency, true);
    let compareToRunConfigurationState: Partial<CountyProjectContainerState>;
    // if we're in compare mode, we need to track the second config
    if (this.props.compareToRunConfiguration) {
      compareToRunConfigurationState = await this.props.setRunConfiguration(this.props.compareToRunConfiguration.projectRunConfigurationId, moment.utc(this.props.compareToRunConfiguration.dateCreated), true, selectedFrequency, true);
    }

    const newState: Partial<CountyProjectContainerState> = {
      ...primaryConfigState,
      ...compareToRunConfigurationState,
      initialDualMembershipMode: false,
      isDualMembershipMode: this.props.isDualMembershipMode,
    };

    this.props.updateRunConfigurationState(newState);
  }

  private handleAttestationRateDaysChange = async (selectedItem: AttestationRateDays) => {
    this.props.setAttestationRateDays(selectedItem);
  }

  private getCompareToRunConfigurationId(trendChartData: AdequacyTrendChartItem[], currentRunConfiguration: ProjectRunConfigurationResult): number {
    if (!trendChartData || trendChartData.length === 0 || trendChartData.length === 1 || !currentRunConfiguration || !trendChartData.some((tcd) => tcd.projectRunConfigurationId !== currentRunConfiguration.projectRunConfigurationId)) {
      return undefined;
    }

    const filteredTrendChartData = trendChartData.filter((tcd) => tcd.projectRunConfigurationId !== currentRunConfiguration.projectRunConfigurationId && !tcd.disabled);

    const latestPublishedRunConfig = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.LatestPublished);

    if (latestPublishedRunConfig) {
      //there is a latest published run configuration to compare to
      return latestPublishedRunConfig.projectRunConfigurationId;
    } else {
      //either there is no latest published run configuration, or it's already selected
      //either way, choose the most recent run configuration if any non-projected exist, otherwise return the projected run configuration
      const termedData = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.Termed);

      const projectedData = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.Projected);

      const projectedTermedData = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.ProjectedTermed);

      const nonNullProjectedDataCount = (termedData ? 1 : 0) + (projectedData ? 1 : 0) + (projectedTermedData ? 1 : 0);

      if (nonNullProjectedDataCount > 0 && filteredTrendChartData.length === nonNullProjectedDataCount) {
        //if the projected item count is equal to the filteredTrendChartData count, then there are no non-projected run configurations. Return whichever is non-null, starting with projection
        //there's only ever one projected run configuration, so this means there are no non-projected configurations available

        if (projectedData) {
          return projectedData.projectRunConfigurationId;
        }

        //if there's no projected, there must be a termed
        return termedData.projectRunConfigurationId;
      }

      //if there's a non-projected run configuration available, return the most recent non-projected. No need to check published or unpublished.
      const sortedTrendChartData = sortBy(filteredTrendChartData.filter((tcd) => tcd.itemType !== TrendChartItemType.Termed && tcd.itemType !== TrendChartItemType.Projected && tcd.itemType !== TrendChartItemType.ProjectedTermed), (tcd) => moment.utc(tcd.date)).reverse();

      return sortedTrendChartData[0] ? sortedTrendChartData[0].projectRunConfigurationId : null;
    }
  }

  private handleDisplayOptionsChange = (displayOptionsDirty: boolean) => {
    if (this.state.displayOptionsDirty !== displayOptionsDirty) {
      this.setState({ displayOptionsDirty });
    }
  }

  private renderDisplayOptions(projectId: number, viewablePanels: TableType[]) {
    return (
      <DisplayOptions
        defaultOpen={true}
        projectId={projectId}
        viewablePanels={viewablePanels}
        onChange={this.handleDisplayOptionsChange}
      />
    );
  }

  private registerTriggerClose = (triggerClose: () => void) => {
    this.setState((prevState) => ({ ...prevState, triggerClose }));
  }

  private getMemberSets = (project: ProjectOptions, runConfiguration: ProjectRunConfiguration) => {
    // We can safely assume there's a companionmembersetid
    const sets: Array<DropdownItem<ProjectRunConfigurationType>> = [];
    const companionRunConfiguration = project.projectRunConfigurations.find((config) => config.projectRunConfigurationId === runConfiguration.companionProjectRunConfigurationId);
    if (runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
      sets.push({
        key: runConfiguration.projectRunConfigurationType,
        name: runConfiguration.memberSetAlias,
      });
      if (companionRunConfiguration) {
        sets.push({
          key: companionRunConfiguration.projectRunConfigurationType,
          name: companionRunConfiguration.memberSetAlias,
        });
      }
    } else if (companionRunConfiguration && companionRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
      sets.push({
        key: companionRunConfiguration.projectRunConfigurationType,
        name: companionRunConfiguration.memberSetAlias,
      });
      sets.push({
        key: runConfiguration.projectRunConfigurationType,
        name: runConfiguration.memberSetAlias,
      });
    }
    return sets;
  }

  private handleMembershipChange = async (selectedItems: Array<DropdownItem<ProjectRunConfigurationType>>, setDualMembershipMode: (dualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean) => void, setLoading: (loadingResults: boolean) => void) => {
    setLoading(true);
    this.setState({
      selectedMemberSetAliases: selectedItems,
    });
    switch (selectedItems.length) {
      case 1:
        setDualMembershipMode(false, selectedItems[0].key);
        break;
      case 2:
        setDualMembershipMode(true);
        this.switchToTrendChart();
        break;
      default:
        return;
    }
  }

  private getSelectedMemberSets = (runConfiguration: ProjectRunConfiguration, isDualMembershipMode: boolean, compareToRunConfiguration: ProjectRunConfiguration) => {
    const result: Array<DropdownItem<ProjectRunConfigurationType>> = [];
    const configs: ProjectRunConfiguration[] = [];
    configs.push(runConfiguration);
    if (isDualMembershipMode) {
      configs.push(compareToRunConfiguration);
    }

    configs.forEach((rc) => {
      if (!!rc) {
        result.push({
          key: rc.projectRunConfigurationType,
          name: rc.memberSetAlias,
        });
      }
    });
    return result;
  }

  // logic to switch from Display Map View to Display Trend Chart when Compare Results is toggled on and in Dual Membership Mode
  private switchToTrendChart = () => {
    if (!this.props.showTrendChart) {
      this.props.onDisplayTrendChartChange();
    }
  }
}

const wrappedOptions = withFeatureFlagContext(withViewableFields(Options));
export { wrappedOptions as Options };
